import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { readCallEntry } from "../../services/bootstrap";
import { CallHubClient, type CallEvent } from "../../services/hub";
import { officerWaitingLabel, isPresenceEvent } from "./officerDirectory";
import { useI18n } from "../../i18n/localization";
import { useCallFlow } from "./useCallFlow";
import { PreCall } from "./PreCall";
import { Calling } from "./Calling";
import { IncomingCall } from "./IncomingCall";
import { CallScreen } from "./CallScreen";
import { CallEnded } from "./CallEnded";
import { DeviceReadiness } from "./DeviceReadiness";
import { LocalWaitingPreview } from "./LocalWaitingPreview";
import { withMicEnabled, DEFAULT_MEDIA_PREFERENCES, type MediaDevicePreferences } from "../../services/mediaDevicePreferences";

// Call-flow container — connects the SignalR hub, maps server events to the pure
// call-flow reducer (useCallFlow / callFlow.ts) and renders the screen for the current
// state (docs/design/modules/04-web-client.md, design/05). The caller's callId is
// bootstrapped from the launch (URL ?callId=); the callee's arrives via incomingCall.
export function CallFlow() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  // Embedded launch exposes window.__VC_BOOTSTRAP__; standalone/dev uses URL params.
  const entry = readCallEntry(window, params);
  const token = entry.token;
  const bootstrapCallId = entry.callId ?? 0;

  const state = useCallFlow((s) => s.state);
  const dispatch = useCallFlow((s) => s.dispatch);
  const hubRef = useRef<CallHubClient | null>(null);
  const selfIdRef = useRef<string>("");
  const connectRef = useRef<Promise<void> | null>(null);
  const [hubReady, setHubReady] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Bumped on every presenceChanged so the field directory (PreCall) refetches its
  // available-only list and reflects officers that became (un)available.
  const [presenceVersion, setPresenceVersion] = useState(0);
  // Resolved identity (rank + name) of this session's officer, shown on the traffic
  // waiting screen so the allocated officer is visibly identified.
  const [selfName, setSelfName] = useState("");
  // Device readiness gate. The hub is NOT connected (so a traffic officer is not published
  // available, and the field directory is not fetched) until the user completes readiness.
  const [ready, setReady] = useState(false);
  const [prefs, setPrefs] = useState<MediaDevicePreferences | null>(null);

  useEffect(() => {
    if (!ready) return; // don't connect/register until readiness is complete (req 3 & 4)
    setHubReady(false);
    setActionError(null);
    const hub = new CallHubClient(onEvent, onResync);
    let disposed = false;
    hubRef.current = hub;

    if (!token) {
      setActionError(t.call.tokenExpired);
      return () => {
        disposed = true;
        void hub.stop();
        hubRef.current = null;
        connectRef.current = null;
      };
    }

    const connect = (async () => {
      await hub.connect(token);
      const me = await hub.register();
      if (disposed) return;
      selfIdRef.current = String(me.userId);
      setSelfName(me.displayName);
      setHubReady(true);
    })();
    connectRef.current = connect;
    connect.catch(() => {
      if (disposed) return;
      setHubReady(false);
      setActionError(t.call.connectFailed);
    });
    return () => {
      disposed = true;
      void hub.stop();
      hubRef.current = null;
      connectRef.current = null;
    };
    // hub identity is fixed by the token; (re)connect once readiness completes
  }, [token, ready]);

  function onEvent(ev: CallEvent): void {
    // Directory presence changes are not call-scoped: refetch the field officer list.
    if (isPresenceEvent(ev.event)) {
      setPresenceVersion((v) => v + 1);
      return;
    }
    const callId = Number((ev.data["callId"] as number | undefined) ?? 0);
    switch (ev.event) {
      case "incomingCall": {
        const from = ev.data["fromUserId"];
        dispatch({ type: "incomingCall", callId, fromName: from != null ? `${t.call.callerFallback} #${from}` : undefined });
        hubRef.current?.setCurrentCall(callId);
        break;
      }
      case "callAccepted":
        dispatch({ type: "accepted", callId, selfPeerId: selfIdRef.current, mediaToken: String(ev.data["mediaToken"] ?? "") });
        hubRef.current?.setCurrentCall(callId);
        break;
      case "callDeclined":
        dispatch({ type: "declined", reason: String(ev.data["reason"] ?? "declined") });
        hubRef.current?.setCurrentCall(null);
        break;
      case "callEnded":
        dispatch({ type: "ended", reason: String(ev.data["reason"] ?? "ended") });
        hubRef.current?.setCurrentCall(null);
        break;
      default:
        break;
    }
  }

  function onResync(_callId: number): void {
    // S2: minimal resync — return to the directory. A full GET /calls state refresh is a follow-up.
    dispatch({ type: "reset" });
  }

  async function placeCall(officerUserId: number, name: string): Promise<void> {
    setActionError(null);
    if (!bootstrapCallId) {
      setActionError(t.call.missingCallId);
      return;
    }
    if (!connectRef.current || !hubRef.current) {
      setActionError(t.call.hubNotReady);
      return;
    }

    try {
      await connectRef.current;
      const ack = await hubRef.current.invite(bootstrapCallId, officerUserId);
      if (!ack.ok) {
        setActionError(ack.error?.message ?? t.call.startFailed);
        return;
      }
      hubRef.current?.setCurrentCall(bootstrapCallId);
      dispatch({ type: "placeCall", callId: bootstrapCallId, selfPeerId: selfIdRef.current, remoteName: name });
    } catch {
      setActionError(t.call.connectFailed);
    }
  }

  async function accept(): Promise<void> {
    if (state.callId) await hubRef.current?.acceptInvite(state.callId);
  }
  async function decline(): Promise<void> {
    if (state.callId) await hubRef.current?.declineInvite(state.callId);
    dispatch({ type: "reset" });
  }
  async function end(): Promise<void> {
    if (state.callId) await hubRef.current?.endCall(state.callId);
  }

  // Mic toggled on the waiting/directory preview → carry into prefs so CallScreen starts muted
  // or live to match. (Camera device/on-off stay as chosen in readiness.)
  const onMicToggle = (next: boolean) => setPrefs((p) => (p ? withMicEnabled(p, next) : p));
  const activePrefs = prefs ?? DEFAULT_MEDIA_PREFERENCES;

  // Readiness gate (MiroTalk-style): show the device check first. Until the user confirms,
  // the hub is not connected, so a traffic officer is not published available and the field
  // directory is neither fetched nor callable. Role is inferred from whether a launch call
  // id is present (field launches carry one; traffic waits for incoming).
  if (!ready) {
    return (
      <DeviceReadiness
        role={bootstrapCallId ? "field" : "traffic"}
        onReady={(p) => { setPrefs(p); setReady(true); }}
      />
    );
  }

  switch (state.screen) {
    case "calling":
      return <Calling remoteName={state.remoteName} onCancel={end} />;
    case "incoming":
      return <IncomingCall fromName={state.remoteName} onAccept={accept} onDecline={decline} />;
    case "incall":
      return state.callId != null && state.peerId && state.mediaToken
        ? <CallScreen callId={state.callId} peerId={state.peerId} mediaToken={state.mediaToken} prefs={prefs ?? undefined} onEnd={end} />
        : null;
    case "ended":
      return <CallEnded reason={state.endReason} onBack={() => dispatch({ type: "reset" })} />;
    case "precall":
    default:
      if (!bootstrapCallId) return <WaitingForIncomingCall officerName={selfName} prefs={activePrefs} onMicToggle={onMicToggle} />;
      return <PreCall onCall={placeCall} canCall={hubReady} actionError={actionError} reloadKey={presenceVersion} prefs={activePrefs} onMicToggle={onMicToggle} />;
  }
}

function WaitingForIncomingCall({ officerName, prefs, onMicToggle }: { officerName: string; prefs: MediaDevicePreferences; onMicToggle: (next: boolean) => void }) {
  const { t, dir } = useI18n();
  const label = officerWaitingLabel(officerName, t.call.officerStation);
  return (
    <main
      dir={dir}
      style={{
        padding: 24, fontFamily: "Segoe UI, Arial, sans-serif", maxWidth: 900, margin: "0 auto",
        display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start"
      }}
    >
      {/* Local preview + mic control for the chosen setup; stacks under the identity on mobile. */}
      <div style={{ flex: "1 1 260px", minWidth: 240, maxWidth: 360 }}>
        <LocalWaitingPreview prefs={prefs} onMicToggle={onMicToggle} />
      </div>

      <div style={{ flex: "2 1 320px", minWidth: 260 }}>
        <h1 style={{ color: "#14305f", fontSize: 19, marginTop: 0 }}>{t.call.waitingTitle}</h1>
        <div style={{ display: "inline-block", background: "#eef3fb", border: "1px solid #d6deea", borderRadius: 8, padding: "8px 14px", margin: "4px 0 12px" }}>
          <span style={{ color: "#64748b", fontSize: 12 }}>{t.call.officerStation}: </span>
          <strong style={{ color: "#14305f", fontSize: 15 }}>{label}</strong>
        </div>
        <p style={{ color: "#5b6b82", fontSize: 14 }}>{t.call.waitingStation}</p>
      </div>
    </main>
  );
}
